<template>
  <section class="reconciliation panel" aria-labelledby="reconciliation-title">
    <div class="section-heading">
      <div>
        <p class="eyebrow">Conciliação necessária</p>
        <h2 id="reconciliation-title">Identifique a matrícula correta</h2>
        <p>O formulário foi recebido, mas aluno e matrícula não puderam ser vinculados com segurança.</p>
      </div>
    </div>

    <dl class="submitted-data">
      <div><dt>Aluno informado</dt><dd>{{ reconciliation.student_name }}</dd></div>
      <div><dt>Responsável financeiro</dt><dd>{{ reconciliation.financial_responsible_name }}</dd></div>
      <div><dt>Período letivo</dt><dd>{{ reconciliation.period_year }}</dd></div>
    </dl>

    <div class="enrollment-field">
      <label for="reconciliation-search">Aluno ou matrícula *</label>
      <div class="combobox">
        <fa :icon="['fas', 'magnifying-glass']" aria-hidden="true" />
        <input
          id="reconciliation-search"
          ref="searchInput"
          v-model.trim="search"
          type="search"
          role="combobox"
          autocomplete="off"
          aria-autocomplete="list"
          aria-controls="reconciliation-options"
          :aria-expanded="options.length > 0"
          :aria-activedescendant="activeIndex >= 0 ? `reconciliation-option-${activeIndex}` : undefined"
          placeholder="Digite nome ou RM"
          @input="scheduleSearch"
          @keydown.down.prevent="moveActive(1)"
          @keydown.up.prevent="moveActive(-1)"
          @keydown.enter.prevent="selectActive"
          @keydown.esc="closeOptions"
        >
        <span v-if="loading" class="loading-label">Buscando...</span>
      </div>
      <small>Selecione uma matrícula do mesmo período informado no formulário.</small>
      <ul v-if="options.length" id="reconciliation-options" class="enrollment-options" role="listbox">
        <li
          v-for="(option, index) in options"
          :id="`reconciliation-option-${index}`"
          :key="option.id"
          role="option"
          :aria-selected="activeIndex === index"
          :class="{ active: activeIndex === index }"
          @mouseenter="activeIndex = index"
          @mousedown.prevent="select(option)"
        >
          <strong>{{ option.student_name }}</strong>
          <span>RM {{ option.student_rm }} · {{ option.unit }} · Período {{ option.period }}</span>
          <small>{{ option.series }} / {{ option.class }} · Matrícula {{ option.code || option.id }}</small>
        </li>
      </ul>
      <p v-else-if="searched && !loading && search.length >= 3" class="empty-search">Nenhuma matrícula elegível encontrada.</p>
    </div>

    <div v-if="selected" class="selected-enrollment">
      <div><strong>{{ selected.student_name }}</strong><span>RM {{ selected.student_rm }} · {{ selected.unit }} · {{ selected.series }} / {{ selected.class }}</span></div>
      <button type="button" @click="clear">Alterar</button>
    </div>

    <div v-if="error" class="form-error" role="alert">{{ error }}</div>
    <footer>
      <button class="button secondary" type="button" @click="$emit('cancel')">Voltar</button>
      <button class="button primary" type="button" :disabled="!selected || submitting" @click="submit">
        {{ submitting ? 'Conciliando...' : 'Conciliar e calcular' }}
      </button>
    </footer>
  </section>
</template>

<script>
import DiscountDecisionService from './discountDecision.service'

export default {
  name: 'DiscountDecisionReconciliation',
  props: {
    requestId: { type: String, required: true },
    reconciliation: { type: Object, required: true }
  },
  emits: ['cancel', 'reconciled'],
  data () {
    return { search: this.reconciliation.student_name || '', options: [], activeIndex: -1, selected: null, loading: false, searched: false, submitting: false, error: null, timer: null, controller: null }
  },
  mounted () { if (this.search.length >= 3) this.searchEnrollments() },
  beforeUnmount () { window.clearTimeout(this.timer); this.controller?.abort() },
  methods: {
    scheduleSearch () {
      this.selected = null
      this.options = []
      this.activeIndex = -1
      this.searched = false
      window.clearTimeout(this.timer)
      this.controller?.abort()
      if (this.search.length >= 3) this.timer = window.setTimeout(this.searchEnrollments, 300)
    },
    async searchEnrollments () {
      this.loading = true
      this.error = null
      const controller = new AbortController()
      this.controller = controller
      try {
        const response = await DiscountDecisionService.searchEnrollments(this.search, controller.signal)
        this.options = (response.data.data || []).filter(option => String(option.period || '').includes(String(this.reconciliation.period_year)))
        this.activeIndex = this.options.length ? 0 : -1
        this.searched = true
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') this.error = error.response?.data?.message || 'Não foi possível buscar as matrículas.'
      } finally {
        if (this.controller === controller) this.loading = false
      }
    },
    moveActive (direction) {
      if (!this.options.length) return
      this.activeIndex = (this.activeIndex + direction + this.options.length) % this.options.length
      this.$nextTick(() => document.getElementById(`reconciliation-option-${this.activeIndex}`)?.scrollIntoView({ block: 'nearest' }))
    },
    selectActive () { if (this.activeIndex >= 0) this.select(this.options[this.activeIndex]) },
    closeOptions () { this.options = []; this.activeIndex = -1 },
    select (option) { this.selected = option; this.search = option.student_name; this.closeOptions() },
    clear () { this.selected = null; this.search = ''; this.searched = false; this.activeIndex = -1; this.$nextTick(() => this.$refs.searchInput?.focus()) },
    async submit () {
      if (!this.selected || this.submitting) return
      this.submitting = true
      this.error = null
      try {
        const response = await DiscountDecisionService.reconcile(this.requestId, this.selected.id)
        this.$emit('reconciled', response.data)
      } catch (error) {
        this.error = error.response?.data?.message || 'Não foi possível conciliar a matrícula.'
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$secondary-text: darken($color-text, 20%);
.reconciliation { margin: 0 auto; max-width: 820px; }
.section-heading h2 { margin: 0 0 $hmg_mini; }.section-heading p { color: $secondary-text; margin: 0; }.eyebrow { color: $color-primary !important; font-size: $font-size-small; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.submitted-data { background: $color-grey; border: 1px solid $color-component; border-radius: 14px; display: grid; gap: $hmg_small; grid-template-columns: repeat(3, 1fr); margin: $hmg_mid 0; padding: $hmg_small; div { display: grid; gap: 3px; } dt { color: $secondary-text; font-size: $font-size-small; } dd { font-weight: 700; margin: 0; } }
.enrollment-field { position: relative; label { display: block; font-weight: 700; margin-bottom: $hmg_mini; } > small { color: $secondary-text; display: block; margin-top: $hmg_mini; } }
.combobox { align-items: center; border: 1px solid $color-mid-grey; border-radius: 14px; display: flex; min-height: 46px; padding: 0 $hmg_small; svg { color: $color-primary; } input { background: transparent; border: 0; flex: 1; font: inherit; min-width: 0; outline: 0; padding: 0 $hmg_small; } &:focus-within { border-color: $color-primary; box-shadow: 0 0 0 3px rgba($color-primary, .12); } }
.loading-label, .empty-search { color: $secondary-text; font-size: $font-size-small; }.enrollment-options { background: $color-secondary; border: 1px solid $color-component; border-radius: 14px; box-shadow: 0 12px 30px rgba($color-dev, .14); left: 0; list-style: none; margin: $hmg_mini 0 0; max-height: 280px; overflow-y: auto; padding: $hmg_mini; position: absolute; right: 0; z-index: 2; li { border-radius: 10px; cursor: pointer; display: grid; gap: 2px; padding: $hmg_small; &.active, &:hover { background: rgba($color-primary, .08); } span, small { color: $secondary-text; } } }
.selected-enrollment { align-items: center; background: $color-grey; border: 1px solid $color-component; border-radius: 14px; display: flex; gap: $hmg_small; justify-content: space-between; margin-top: $hmg_small; padding: $hmg_small; div { display: grid; gap: 2px; } span { color: $secondary-text; } button { background: transparent; border: 0; color: $color-primary; cursor: pointer; font-weight: 700; } }
.form-error { color: $color-error-2; font-weight: 600; margin-top: $hmg_small; }.panel { background: $color-secondary; border: 1px solid $color-component; border-radius: 16px; padding: $hmg_mid; }
footer { border-top: 1px solid $color-component; display: flex; gap: $hmg_small; justify-content: flex-end; margin-top: $mg; padding-top: $hmg_mid; }.button { border: 1px solid transparent; border-radius: 14px; cursor: pointer; font: inherit; font-weight: 700; min-height: 46px; padding: 0 $hmg_mid; &.primary { background: $color-primary; color: $color-secondary; } &.secondary { background: $color-secondary; border-color: $color-mid-grey; color: $color-dev; } &:disabled { cursor: not-allowed; opacity: .55; } }
@media (max-width: 720px) { .submitted-data { grid-template-columns: 1fr; } footer { flex-direction: column-reverse; }.button { width: 100%; } }
</style>
